import React, { useEffect, useState, useRef } from "react";
import { useSearchParams, useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import { useApp } from "@/context/AppContext";
import { CheckCircle2, XCircle, Loader2, Star, Sparkles } from "lucide-react";
import { Button } from "@/components/ui/button";

export default function PaymentSuccess() {
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const { lang, checkAuth } = useApp();
  const [status, setStatus] = useState("polling"); // polling | paid | timeout | failed
  const pollCount = useRef(0);

  const sessionId = params.get("session_id");
  const T = (en, es) => (lang === "es" ? es : en);

  useEffect(() => {
    if (!sessionId) {
      setStatus("failed");
      return;
    }
    let cancelled = false;
    const poll = async () => {
      if (cancelled) return;
      pollCount.current += 1;
      try {
        const res = await api.get(`/payments/status/${sessionId}`);
        if (res.data.payment_status === "paid") {
          await checkAuth();
          setStatus("paid");
          return;
        }
        if (["failed", "expired", "refunded"].includes(res.data.payment_status)) {
          setStatus("failed");
          return;
        }
        if (pollCount.current >= 20) {
          setStatus("timeout");
          return;
        }
        setTimeout(poll, 2000);
      } catch {
        if (pollCount.current >= 20) setStatus("timeout");
        else setTimeout(poll, 2000);
      }
    };
    poll();
    return () => { cancelled = true; };
  }, [sessionId, checkAuth]);

  return (
    <div className="min-h-[60vh] flex items-center justify-center">
      <div
        data-testid="payment-result"
        className="max-w-md w-full rounded-3xl border border-[var(--border)] bg-[var(--surface-elevated)] p-10 text-center"
      >
        {status === "polling" && (
          <>
            <Loader2 className="w-10 h-10 mx-auto text-[var(--accent)] animate-spin" strokeWidth={1.5} />
            <h2 className="font-serif text-3xl mt-6 tracking-tight">
              {T("Confirming your payment…", "Confirmando tu pago…")}
            </h2>
            <p className="mt-3 text-[var(--text-secondary)] text-sm leading-relaxed">
              {T(
                "Hang tight — this usually takes a few seconds.",
                "Un momento — esto suele tardar unos segundos."
              )}
            </p>
          </>
        )}

        {status === "paid" && (
          <>
            <div className="relative inline-block">
              <CheckCircle2 className="w-14 h-14 mx-auto text-[var(--accent)]" strokeWidth={1.5} />
              <Star className="w-5 h-5 absolute -top-1 -right-1 text-[var(--accent)] fill-[var(--accent)]" strokeWidth={1.5} />
            </div>
            <h2 className="font-serif text-3xl mt-6 tracking-tight" data-testid="payment-success-title">
              {T("You're now a Supporter", "¡Ya eres Sostenedor!")}
            </h2>
            <p className="mt-3 text-[var(--text-secondary)] leading-relaxed">
              {T(
                "Thank you for keeping FaithCycle alive. Your early-access perks are unlocked.",
                "Gracias por mantener a FaithCycle viva. Tus beneficios de acceso anticipado están desbloqueados."
              )}
            </p>
            <div className="mt-8 flex flex-col gap-2">
              <Button
                data-testid="go-early-access-btn"
                onClick={() => navigate("/early-access")}
                className="w-full h-11 rounded-full bg-[var(--accent)] hover:bg-[var(--accent)]/90 text-white"
              >
                <Sparkles className="w-4 h-4 mr-2" strokeWidth={1.5} />
                {T("Explore early access", "Ver acceso anticipado")}
              </Button>
              <Button
                variant="ghost"
                onClick={() => navigate("/home")}
                className="w-full h-11 rounded-full text-[var(--text-secondary)]"
              >
                {T("Back to home", "Volver al inicio")}
              </Button>
            </div>
          </>
        )}

        {status === "timeout" && (
          <>
            <Loader2 className="w-10 h-10 mx-auto text-[var(--text-secondary)]" strokeWidth={1.5} />
            <h2 className="font-serif text-3xl mt-6 tracking-tight">
              {T("Still confirming…", "Aún confirmando…")}
            </h2>
            <p className="mt-3 text-[var(--text-secondary)] text-sm leading-relaxed">
              {T(
                "Payments can take a moment. If your card was charged, refresh this page — your Supporter status will appear shortly.",
                "Los pagos pueden tardar un momento. Si te cobraron la tarjeta, actualiza esta página — tu estado de Sostenedor aparecerá pronto."
              )}
            </p>
            <Button
              onClick={() => window.location.reload()}
              className="mt-8 rounded-full bg-[var(--text-primary)] text-[var(--bg)]"
            >
              {T("Refresh", "Actualizar")}
            </Button>
          </>
        )}

        {status === "failed" && (
          <>
            <XCircle className="w-14 h-14 mx-auto text-[var(--text-secondary)]" strokeWidth={1.5} />
            <h2 className="font-serif text-3xl mt-6 tracking-tight">
              {T("Payment didn't go through", "El pago no se completó")}
            </h2>
            <p className="mt-3 text-[var(--text-secondary)] text-sm leading-relaxed">
              {T(
                "No worries — nothing was charged. You can try again anytime.",
                "Tranquilo — no se cobró nada. Puedes intentarlo cuando quieras."
              )}
            </p>
            <Button
              onClick={() => navigate("/support")}
              className="mt-8 rounded-full bg-[var(--text-primary)] text-[var(--bg)]"
            >
              {T("Try again", "Intentar de nuevo")}
            </Button>
          </>
        )}
      </div>
    </div>
  );
}
